// Pages/UploadPage.tsx (Fixed version)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { uploadImageToBackend } from "../utils/API";

export default function UploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadedData, setUploadedData] = useState<any>(null);

  const previewUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  function onPickFile(f: File | null) {
    setFile(f);
    setResult(null);
    setError(null);
    setUploadedData(null);
  }

  const handleAnalyze = async (file: File) => {
    setIsLoading(true);
    setError(null);

    try {
      const userId = localStorage.getItem("userId");
      const response = await uploadImageToBackend(file, userId);
      console.log("Full response:", response);

      if (response.success && response.data) {
        // Store the full response data
        setUploadedData(response.data);
        // Set the analysis result for display
        setResult(response.data.analysis);
      } else {
        setError(response.message || "Analysis failed");
      }
    } catch (error) {
      console.error("Analysis failed:", error);
      setError("Failed to analyze card. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setUploadedData(null);
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-slate-50 py-10">
      {/* Decorative background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-blue-200/60 to-purple-200/60 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-indigo-200/60 to-sky-200/60 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.06)_1px,transparent_0)] [background-size:22px_22px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Show Result Page if analysis is complete */}
        {result ? (
          <div className="space-y-8">
            {/* Back Button */}
            {/* <button
            onClick={resetForm}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Upload
          </button> */}

            {/* Results Section */}
            <div>
              {/* Header Section */}
              <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-1">
                      Result
                    </h1>
                    <p className="text-sm text-slate-600">
                      {uploadedData?.created_at
                        ? new Date(uploadedData.created_at).toLocaleString()
                        : new Date().toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={resetForm}
                      className="px-4 py-2 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-emerald-500 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Grade another card
                    </button>
                    <button
                      onClick={() => navigate("/history")}
                      className="px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      History
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Image Section */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700">
                      Card Image
                    </h3>
                  </div>
                  <div className="p-4">
                    <div className="overflow-hidden rounded-xl bg-slate-100">
                      <img
                        src={uploadedData?.image_url}
                        alt="Graded card"
                        className="w-full max-h-[520px] object-contain"
                      />
                    </div>
                  </div>
                </div>

                {/* Results Section */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700">
                      Grading Results
                    </h3>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Grade and Price */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 text-center">
                        <div className="text-xs text-slate-600 mb-1">
                          AI Grade
                        </div>
                        <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 bg-clip-text text-transparent">
                          {result?.grade?.score || "N/A"}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-indigo-50 to-sky-50 rounded-xl p-4 text-center">
                        <div className="text-xs text-slate-600 mb-1">Price</div>
                        <div className="text-2xl font-bold text-indigo-700">
                          {result?.market_price?.graded_price || "N/A"}
                        </div>
                        {/* <div className="text-xs text-gray-500 mt-1">
                        Raw: {result?.market_price?.raw_price || "N/A"}
                      </div> */}
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <svg
                          className="w-5 h-5 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h4 className="text-sm font-semibold text-slate-700">
                          Recommendation
                        </h4>
                      </div>
                      <div
                        className={`
                      inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium
                      ${
                        result?.recommendation?.action === "Hold"
                          ? "bg-green-100 text-green-800"
                          : result?.recommendation?.action === "Buy"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-amber-100 text-amber-800"
                      }
                    `}
                      >
                        <span className="mr-2">
                          {result?.recommendation?.action === "Hold"
                            ? "✅"
                            : result?.recommendation?.action === "Buy"
                              ? "📈"
                              : "⚠️"}
                        </span>
                        {result?.recommendation?.action || "Hold"}
                        {result?.recommendation?.confidence && (
                          <span className="ml-2 text-xs opacity-75">
                            ({result.recommendation.confidence} confidence)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Card Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">
                          Card Name
                        </div>
                        <div className="text-sm font-medium text-slate-900">
                          {result?.card_name || "Unknown"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">
                          Year / Set
                        </div>
                        <div className="text-sm font-medium text-slate-900">
                          {result?.year || "Unknown"} /{" "}
                          {result?.set || "Unknown"}
                        </div>
                      </div>
                    </div>

                    {/* Price Trend */}
                    {result?.market_price?.price_trend && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                            />
                          </svg>
                          <h4 className="text-sm font-semibold text-slate-700">
                            Price Trend
                          </h4>
                        </div>
                        <span
                          className={`
                        text-xs px-2 py-1 rounded-full
                        ${
                          result.market_price.price_trend === "Rising"
                            ? "bg-green-100 text-green-700"
                            : result.market_price.price_trend === "Declining"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-700"
                        }
                      `}
                        >
                          {result.market_price.price_trend}
                        </span>
                      </div>
                    )}

                    {/* Condition Details */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <svg
                          className="w-5 h-5 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                        <h4 className="text-sm font-semibold text-slate-700">
                          Condition Details
                        </h4>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-slate-50 rounded-lg p-2">
                          <span className="text-xs text-slate-500">
                            Overall:
                          </span>
                          <p className="font-medium text-slate-900 text-xs mt-1">
                            {result?.grade?.condition || "N/A"}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <span className="text-xs text-slate-500">
                            Centering:
                          </span>
                          <p className="font-medium text-slate-900">
                            {result?.grade?.centering || "N/A"}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <span className="text-xs text-slate-500">
                            Corners:
                          </span>
                          <p className="font-medium text-slate-900">
                            {result?.grade?.corners || "N/A"}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <span className="text-xs text-slate-500">Edges:</span>
                          <p className="font-medium text-slate-900">
                            {result?.grade?.edges || "N/A"}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 col-span-2">
                          <span className="text-xs text-slate-500">
                            Surface:
                          </span>
                          <p className="font-medium text-slate-900">
                            {result?.grade?.surface || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Reason */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <svg
                          className="w-5 h-5 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h4 className="text-sm font-semibold text-slate-700">
                          Analysis Reason
                        </h4>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-lg p-4">
                        {result?.recommendation?.reason ||
                          result?.grade?.condition ||
                          "Analysis complete"}
                      </p>
                    </div>

                    {/* Investment Potential */}
                    {result?.recommendation?.investment_potential && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <h4 className="text-sm font-semibold text-slate-700">
                            Investment Potential
                          </h4>
                        </div>
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                          {result.recommendation.investment_potential}
                        </span>
                      </div>
                    )}

                    {/* Rarity & Demand */}
                    {(result?.detailed_analysis?.rarity ||
                      result?.detailed_analysis?.market_demand) && (
                      <div className="flex gap-4">
                        {result.detailed_analysis.rarity && (
                          <div>
                            <span className="text-xs text-slate-500">
                              Rarity:
                            </span>
                            <p className="text-sm font-medium text-slate-900">
                              {result.detailed_analysis.rarity}
                            </p>
                          </div>
                        )}
                        {result.detailed_analysis.market_demand && (
                          <div>
                            <span className="text-xs text-slate-500">
                              Market Demand:
                            </span>
                            <p className="text-sm font-medium text-slate-900">
                              {result.detailed_analysis.market_demand}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Comparable Sales */}
                    {result?.market_price?.comparable_sales && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <svg
                            className="w-4 h-4 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                            />
                          </svg>
                          <h4 className="text-sm font-semibold text-slate-700">
                            Comparable Sales
                          </h4>
                        </div>
                        <p className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
                          {result.market_price.comparable_sales}
                        </p>
                      </div>
                    )}

                    {/* Defects */}
                    {result?.grade?.defects &&
                      result.grade.defects.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <svg
                              className="w-5 h-5 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <h4 className="text-sm font-semibold text-slate-700">
                              Detected Defects
                            </h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {result.grade.defects.map(
                              (defect: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full"
                                >
                                  {defect}
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Rest of your upload UI remains the same...
          <>
            {/* Header Section */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                AI Trading Card Grader
              </h1>
              <p className="text-slate-600">
                Upload a clear photo of the card (front). We'll detect defects
                and return a PSA-style grade + price estimate.
              </p>
            </div>

            {/* Loading Overlay */}
            {isLoading && (
              <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md px-9 py-10 flex flex-col items-center text-center">
                  {/* Logo & Brand (Matching Login Page) */}
                  <div className="flex flex-col items-center mb-2">
                    <div className="flex items-center no-underline">
                      {/* Logo */}
                      <img
                        src="/card-grader-logo.png"
                        alt="CardGrader Logo"
                        className="w-[100px] h-[100px] rounded-xl object-contain ml-[-30px]"
                      />

                      {/* Name */}
                      <div className="flex flex-col leading-none ml-[-15px]">
                        <span className="text-2xl font-black tracking-tight bg-gradient-to-r from-emerald-800 via-emerald-600 to-emerald-400 bg-clip-text text-transparent">
                          CardGrader
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Spinner */}
                  <div className="relative mb-8 mt-4">
                    <div className="w-16 h-16 border-4 border-emerald-50/50 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    Analyzing your card...
                  </h3>
                  <p className="text-sm text-gray-400 font-medium mb-10">
                    Please wait while our AI detects defects and estimates the
                    grade.
                  </p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm text-red-800">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="ml-auto text-red-600 hover:text-red-800"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Upload Area */}
            <div
              className={`
              relative rounded-2xl border-2 border-dashed p-12 transition-all duration-200
	              ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"}
	              ${isLoading ? "opacity-50 pointer-events-none" : ""}
	            `}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(false);
                const dropped = e.dataTransfer.files?.[0] || null;
                if (dropped) onPickFile(dropped);
              }}
            >
              <div className="flex flex-col items-center text-center">
                {/* Upload Icon */}
                <div className="mb-4">
                  <svg
                    className={`w-16 h-16 ${dragOver ? "text-emerald-500" : "text-gray-400"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Drag & drop an image here
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  or click to choose a file (JPG/PNG up to 10MB)
                </p>

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onPickFile(e.target.files?.[0] || null)}
                />

                <button
                  onClick={() => inputRef.current?.click()}
                  disabled={isLoading}
                  className="px-6 py-2.5 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-emerald-500 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Choose image
                </button>

                {file && !isLoading && (
                  <div className="mt-8 w-full animate-fade-in-up">
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-5 h-5 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-slate-700">
                          Selected:{" "}
                          <span className="font-medium text-slate-900">
                            {file.name}
                          </span>
                        </span>
                        <span className="text-xs text-slate-500">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        onClick={() => setFile(null)}
                        className="text-sm text-red-500 hover:text-red-700 transition-colors flex items-center space-x-1"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        <span>Remove</span>
                      </button>
                    </div>

                    {previewUrl && (
                      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                        <img
                          src={previewUrl}
                          alt="Card preview"
                          className="w-full max-h-[480px] object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex items-center justify-end gap-3">
              <button
                onClick={() => navigate("/history")}
                disabled={isLoading}
                className="px-6 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                View History
              </button>
              <button
                disabled={!file || isLoading}
                onClick={() => {
                  if (!file) return;
                  handleAnalyze(file);
                }}
                className={`
                px-6 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2
                ${
                  file && !isLoading
                    ? "bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 text-white hover:from-emerald-500 hover:to-emerald-700 shadow-sm hover:shadow-md"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }
              `}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      />
                    </svg>
                    <span>Analyze Card</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
